import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { TriangleAlert } from 'lucide-react';
import { api } from '@/lib/api';
import { useConfirm } from '@/components/ui/confirm';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Field, Select, Textarea } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import {
  apiError,
  apiStatus,
  parseConflictMessage,
  type EmployeeRef,
  type RoleRef,
  type ScheduledShift,
  type ShiftTemplate,
  type ShiftWriteResult,
} from './schedulingLib';

interface Props {
  open: boolean;
  onClose: () => void;
  scheduleId: string;
  /** Create mode: the day this shift is being added to. Edit mode: ignored — the shift's own date is used. */
  date: string;
  shift?: ScheduledShift | null;
  employees: EmployeeRef[];
  roles: RoleRef[];
  templates: ShiftTemplate[];
  canManage: boolean;
  onSaved: () => void;
}

const BLANK_TIMES = { startTime: '', endTime: '' };

export default function ShiftDialog({
  open,
  onClose,
  scheduleId,
  date,
  shift,
  employees,
  roles,
  templates,
  canManage,
  onSaved,
}: Props) {
  const { t } = useTranslation();
  const confirmDialog = useConfirm();
  const editing = Boolean(shift);

  const [employeeId, setEmployeeId] = useState('');
  const [roleId, setRoleId] = useState('');
  const [templateId, setTemplateId] = useState('');
  const [times, setTimes] = useState(BLANK_TIMES);
  const [breakMinutes, setBreakMinutes] = useState('0');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [cancelling, setCancelling] = useState(false);
  const [conflicts, setConflicts] = useState<{ items: string[]; retryable: boolean } | null>(null);

  useEffect(() => {
    if (!open) return;
    if (shift) {
      setEmployeeId(shift.employeeId ?? '');
      setRoleId(shift.roleId ?? '');
      setTemplateId('');
      // Edited in the branch's own wall clock — see `formatWallTime` for why.
      setTimes({
        startTime: new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: shift.timezone,
        }).format(new Date(shift.startsAt)),
        endTime: new Intl.DateTimeFormat('en-GB', {
          hour: '2-digit',
          minute: '2-digit',
          hour12: false,
          timeZone: shift.timezone,
        }).format(new Date(shift.endsAt)),
      });
      setBreakMinutes(String(shift.breakMinutes));
      setNotes(shift.notes ?? '');
    } else {
      setEmployeeId('');
      setRoleId('');
      setTemplateId('');
      setTimes(BLANK_TIMES);
      setBreakMinutes('0');
      setNotes('');
    }
    setConflicts(null);
  }, [open, shift]);

  const applyTemplate = (id: string) => {
    setTemplateId(id);
    const tpl = templates.find((tp) => tp.id === id);
    if (!tpl) return;
    setTimes({ startTime: tpl.startTime, endTime: tpl.endTime });
    setBreakMinutes(String(tpl.breakMinutes));
    if (tpl.roleId) setRoleId(tpl.roleId);
  };

  const submit = async (acceptWarnings: boolean) => {
    if (!times.startTime || !times.endTime) return;
    setSaving(true);
    setConflicts(null);
    try {
      const basePayload = {
        employeeId: employeeId || undefined,
        roleId: roleId || undefined,
        breakMinutes: Number(breakMinutes) || 0,
        notes: notes.trim() || undefined,
        acceptWarnings,
      };
      let result: ShiftWriteResult;
      if (editing && shift) {
        const { data } = await api.patch<ShiftWriteResult>(`/scheduling/shifts/${shift.id}`, {
          ...basePayload,
          employeeId: employeeId || null,
          startTime: times.startTime,
          endTime: times.endTime,
        });
        result = data;
      } else {
        const { data } = await api.post<ShiftWriteResult>(
          `/scheduling/schedules/${scheduleId}/shifts`,
          {
            ...basePayload,
            templateId: templateId || undefined,
            date,
            startTime: times.startTime,
            endTime: times.endTime,
          },
        );
        result = data;
      }
      if (result.warnings?.length) {
        // Saved despite advisory conflicts already accepted this round — tell
        // the manager what they just accepted rather than only staying silent.
        toast.warning(
          t('scheduling.shift.savedWithWarnings', { count: result.warnings.length }),
        );
      } else {
        toast.success(t(editing ? 'scheduling.shift.updated' : 'scheduling.shift.created'));
      }
      onSaved();
      onClose();
    } catch (e) {
      if (apiStatus(e) === 409) {
        const parsed = parseConflictMessage(apiError(e, ''));
        if (parsed.items.length) {
          setConflicts(parsed);
          setSaving(false);
          return;
        }
      }
      toast.error(apiError(e, t('scheduling.shift.saveFailed')));
    } finally {
      setSaving(false);
    }
  };

  const cancelShift = async () => {
    if (!shift) return;
    const ok = await confirmDialog.confirm({
      title: t('scheduling.shift.cancelConfirmTitle'),
      body: t('scheduling.shift.cancelConfirmBody'),
      confirmLabel: t('scheduling.shift.cancelShift'),
      tone: 'danger',
    });
    if (!ok) return;
    setCancelling(true);
    try {
      await api.delete(`/scheduling/shifts/${shift.id}`);
      toast.success(t('scheduling.shift.cancelled'));
      onSaved();
      onClose();
    } catch (e) {
      toast.error(apiError(e, t('scheduling.shift.cancelFailed')));
    } finally {
      setCancelling(false);
    }
  };

  const blocking = conflicts && !conflicts.retryable;

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={t(editing ? 'scheduling.shift.editTitle' : 'scheduling.shift.addTitle')}
      description={editing ? undefined : date}
      className="max-w-lg"
      footer={
        canManage ? (
          <div className="flex w-full flex-wrap items-center justify-between gap-2">
            {editing ? (
              <Button variant="danger" size="sm" disabled={cancelling} onClick={() => void cancelShift()}>
                {t('scheduling.shift.cancelShift')}
              </Button>
            ) : (
              <span />
            )}
            <div className="flex gap-2">
              <Button variant="secondary" onClick={onClose}>
                {t('common.cancel')}
              </Button>
              {conflicts?.retryable ? (
                <Button variant="danger" disabled={saving} onClick={() => void submit(true)}>
                  {t('scheduling.shift.saveAnyway')}
                </Button>
              ) : (
                <Button
                  disabled={saving || Boolean(blocking) || !times.startTime || !times.endTime}
                  onClick={() => void submit(false)}
                >
                  {t('common.save')}
                </Button>
              )}
            </div>
          </div>
        ) : undefined
      }
    >
      <fieldset disabled={!canManage} className="space-y-4">
        {!editing && templates.length > 0 && (
          <Field label={t('scheduling.shift.template')}>
            <Select value={templateId} onChange={(e) => applyTemplate(e.target.value)}>
              <option value="">{t('scheduling.shift.noTemplate')}</option>
              {templates.map((tpl) => (
                <option key={tpl.id} value={tpl.id}>
                  {tpl.name} ({tpl.startTime}–{tpl.endTime})
                </option>
              ))}
            </Select>
          </Field>
        )}

        <Field label={t('scheduling.shift.employee')} hint={t('scheduling.shift.employeeHint')}>
          <Select value={employeeId} onChange={(e) => setEmployeeId(e.target.value)}>
            <option value="">{t('scheduling.shift.openShift')}</option>
            {employees.map((emp) => (
              <option key={emp.id} value={emp.id}>
                {emp.firstName} {emp.lastName ?? ''} ({emp.employeeCode})
              </option>
            ))}
          </Select>
        </Field>

        <Field label={t('scheduling.shift.role')}>
          <Select value={roleId} onChange={(e) => setRoleId(e.target.value)}>
            <option value="">{t('scheduling.shift.noRole')}</option>
            {roles.map((role) => (
              <option key={role.id} value={role.id}>
                {role.name}
              </option>
            ))}
          </Select>
        </Field>

        <div className="grid grid-cols-2 gap-3">
          <Field label={t('scheduling.shift.startTime')}>
            <Input
              type="time"
              value={times.startTime}
              onChange={(e) => setTimes((prev) => ({ ...prev, startTime: e.target.value }))}
            />
          </Field>
          <Field label={t('scheduling.shift.endTime')} hint={t('scheduling.shift.endTimeHint')}>
            <Input
              type="time"
              value={times.endTime}
              onChange={(e) => setTimes((prev) => ({ ...prev, endTime: e.target.value }))}
            />
          </Field>
        </div>

        <Field label={t('scheduling.shift.breakMinutes')}>
          <Input
            type="number"
            min={0}
            max={480}
            value={breakMinutes}
            onChange={(e) => setBreakMinutes(e.target.value)}
          />
        </Field>

        <Field label={t('scheduling.shift.notes')}>
          <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>

        {conflicts && (
          <div
            className={`flex gap-2 rounded-lg border p-3 text-sm ${
              blocking
                ? 'border-red-300 bg-red-50 text-red-800'
                : 'border-amber-300 bg-amber-50 text-amber-800'
            }`}
          >
            <TriangleAlert className="mt-0.5 h-4 w-4 shrink-0" />
            <div className="space-y-1">
              <p className="font-medium">
                {t(blocking ? 'scheduling.shift.blockingTitle' : 'scheduling.shift.advisoryTitle')}
              </p>
              <ul className="list-disc ps-4">
                {conflicts.items.map((item, i) => (
                  <li key={i}>{item}</li>
                ))}
              </ul>
              {!blocking && (
                <p>{t('scheduling.shift.advisoryHint')}</p>
              )}
            </div>
          </div>
        )}

        {!canManage && (
          <div className="flex items-center gap-2 text-sm text-ink-muted">
            <Badge tone="neutral">{t('scheduling.readOnly')}</Badge>
          </div>
        )}
      </fieldset>
    </Dialog>
  );
}
